// R-multiple math.
//
// R is the trader's standard unit of risk. If a trader risks $100 on a trade
// (the dollar distance from entry to initial stop, multiplied by lot size
// and pip value), then a +$150 outcome is +1.5R and a −$100 outcome is −1R.
//
// We sidestep per-instrument pip math by computing R from PRICES alone:
//   BUY:  R = (exit − entry) / (entry − initial_sl)
//   SELL: R = (entry − exit) / (initial_sl − entry)
//
// This is exact: it doesn't need pip value, lot size, or contract size,
// because both the numerator (P&L per unit price move) and denominator
// (risk per unit price move) cancel those factors. The only requirement is
// that an initial SL was set at fill time.

import type { ClosedPosition } from '@/types/veritas';

export type RAnalysis = {
  r: number | null;        // null when initial_sl wasn't set or numbers are invalid
  riskPriceDistance: number | null;
};

export function rMultiple(p: ClosedPosition): RAnalysis {
  if (
    p.entry_price == null
    || p.exit_price == null
    || p.initial_sl == null
    || p.initial_sl === 0
  ) {
    return { r: null, riskPriceDistance: null };
  }
  const isBuy = p.side_code === 0;
  const riskPriceDistance = isBuy
    ? p.entry_price - p.initial_sl
    : p.initial_sl - p.entry_price;
  if (!Number.isFinite(riskPriceDistance) || riskPriceDistance <= 0) {
    return { r: null, riskPriceDistance: null };
  }
  const profitPriceDistance = isBuy
    ? p.exit_price - p.entry_price
    : p.entry_price - p.exit_price;
  const r = profitPriceDistance / riskPriceDistance;
  if (!Number.isFinite(r)) return { r: null, riskPriceDistance };
  return { r, riskPriceDistance };
}

// Aggregate stats across a set of closed positions.
export type RStats = {
  count: number;
  withR: number;             // how many positions had a usable R
  avgR: number | null;       // mean R across positions with R defined
  expectancyR: number | null;
  winRate: number | null;
  bestR: number | null;
  worstR: number | null;
};

export function rStats(positions: ClosedPosition[]): RStats {
  const rs: number[] = [];
  for (const p of positions) {
    const { r } = rMultiple(p);
    if (r != null) rs.push(r);
  }
  const count = positions.length;
  const withR = rs.length;
  if (withR === 0) {
    return { count, withR, avgR: null, expectancyR: null, winRate: null, bestR: null, worstR: null };
  }
  const sum = rs.reduce((a, b) => a + b, 0);
  const avgR = sum / withR;
  const wins = rs.filter((r) => r > 0);
  const losses = rs.filter((r) => r <= 0);
  const winRate = wins.length / withR;
  const avgWinR = wins.length ? wins.reduce((a, b) => a + b, 0) / wins.length : 0;
  const avgLossR = losses.length ? losses.reduce((a, b) => a + b, 0) / losses.length : 0;
  const expectancyR = winRate * avgWinR + (1 - winRate) * avgLossR;
  return {
    count,
    withR,
    avgR,
    expectancyR,
    winRate,
    bestR: Math.max(...rs),
    worstR: Math.min(...rs),
  };
}

// Mistake-tag correlation: average R for positions tagged with a given
// mistake vs positions NOT tagged with it. Returns one row per mistake.
export type MistakeRow = {
  tagName: string;
  taggedCount: number;
  taggedAvgR: number | null;
  taggedAvgPnL: number;
  untaggedAvgR: number | null;
  untaggedAvgPnL: number;
  // Difference: positive = the mistake hurts, negative = the mistake helps
  rDelta: number | null;
  pnlDelta: number;
};

export function mistakeCorrelation(
  positions: ClosedPosition[],
  positionTags: Map<number, Set<string>>,  // position_ticket → tag_id set
  mistakeTags: { id: string; name: string }[],
): MistakeRow[] {
  const out: MistakeRow[] = [];
  for (const tag of mistakeTags) {
    const tagged: ClosedPosition[] = [];
    const untagged: ClosedPosition[] = [];
    for (const p of positions) {
      const tags = positionTags.get(p.position_ticket);
      if (tags?.has(tag.id)) tagged.push(p);
      else untagged.push(p);
    }
    const tStats = rStats(tagged);
    const uStats = rStats(untagged);
    const tPnL = tagged.reduce((a, p) => a + p.realized_profit, 0);
    const uPnL = untagged.reduce((a, p) => a + p.realized_profit, 0);
    const taggedAvgPnL = tagged.length ? tPnL / tagged.length : 0;
    const untaggedAvgPnL = untagged.length ? uPnL / untagged.length : 0;
    out.push({
      tagName: tag.name,
      taggedCount: tagged.length,
      taggedAvgR: tStats.avgR,
      taggedAvgPnL,
      untaggedAvgR: uStats.avgR,
      untaggedAvgPnL,
      rDelta: tStats.avgR != null && uStats.avgR != null ? tStats.avgR - uStats.avgR : null,
      pnlDelta: taggedAvgPnL - untaggedAvgPnL,
    });
  }
  return out;
}

// Per-tag analytics: count, win rate, total P&L, expectancy in R.
export type TagStatsRow = {
  tagId: string;
  tagName: string;
  category: string | null;
  color: string;
  count: number;
  winRate: number;
  totalPnL: number;
  expectancyR: number | null;
};

export function perTagStats(
  positions: ClosedPosition[],
  positionTags: Map<number, Set<string>>,
  tags: { id: string; name: string; color: string; category: string | null }[],
): TagStatsRow[] {
  return tags.map((t) => {
    const subset = positions.filter((p) => positionTags.get(p.position_ticket)?.has(t.id));
    const wins = subset.filter((p) => p.realized_profit > 0).length;
    const winRate = subset.length ? (wins / subset.length) * 100 : 0;
    const totalPnL = subset.reduce((a, p) => a + p.realized_profit, 0);
    const stats = rStats(subset);
    return {
      tagId: t.id,
      tagName: t.name,
      category: t.category,
      color: t.color,
      count: subset.length,
      winRate,
      totalPnL,
      expectancyR: stats.expectancyR,
    };
  }).sort((a, b) => b.count - a.count);
}
